<template>
  <div class="meter-valve-control">
    <v-switch
      v-model="switchPosition"
      inset
      class="mt-1 mb-0"
      hide-details
      :loading="submitting"
      :disabled="submitting || showConfirm"
      :false-value="0"
      :true-value="1"
      :label="statusLabel"
      @change="requestChange"
    ></v-switch>
    <confirm-dialog
      :show-dialog="showConfirm"
      :is-agree-button-loading="submitting"
      message="Close this meter’s valve?"
      agree-text="Close valve"
      @cancel="cancelChange"
      @agree="submitChange"
    />
  </div>
</template>

<script>
import axios from 'axios'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

export default {
  components: { ConfirmDialog },
  props: {
    meter: { type: Object, required: true },
  },
  data() {
    return {
      switchPosition: 0,
      requestedPosition: null,
      optimisticPosition: null,
      submitting: false,
      showConfirm: false,
      pollTimer: null,
      stopped: false,
      requestVersion: 0,
    }
  },
  computed: {
    command() {
      return this.meter.valve_command
    },
    observedPosition() {
      return Number(this.meter.valve_status) === 1 ? 1 : 0
    },
    pending() {
      return !!this.command && (['pending', 'submitting', 'accepted', 'uncertain', 'blocked'].includes(this.command.status) || (this.command.awaiting_observation && this.observedPosition !== (this.command.desired_valve_action === 'open' ? 1 : 0)))
    },
    displayedPosition() {
      if (this.optimisticPosition !== null) return this.optimisticPosition
      if (this.pending) return this.command.desired_valve_action === 'open' ? 1 : 0

      return this.observedPosition
    },
    statusLabel() {
      return this.displayedPosition === 1 ? 'Open' : 'Closed'
    },
  },
  watch: {
    displayedPosition: {
      handler(value) { this.switchPosition = value },
      immediate: true,
    },
    'meter.valve_command': {
      handler() { this.scheduleRefresh() },
      immediate: true,
      deep: true,
    },
    'meter.id': function resetMeter() {
      this.requestVersion += 1
      this.submitting = false
      this.optimisticPosition = null
      this.cancelChange()
      this.scheduleRefresh()
    },
  },
  beforeDestroy() {
    this.stopped = true
    clearTimeout(this.pollTimer)
  },
  methods: {
    requestChange(value) {
      this.$nextTick(() => { this.switchPosition = this.displayedPosition })
      if (this.submitting || this.showConfirm || Number(value) === this.displayedPosition) return
      this.requestedPosition = Number(value)
      if (this.requestedPosition === 0) this.showConfirm = true
      else this.submitChange()
    },
    cancelChange() {
      this.showConfirm = false
      this.requestedPosition = null
      this.switchPosition = this.displayedPosition
    },
    applyStatus(data) {
      const wasPending = this.pending || this.optimisticPosition !== null
      this.meter.valve_status = data.valve_status
      this.$set(this.meter, 'valve_command', data.valve_command || null)
      if (!this.pending) {
        this.optimisticPosition = null
        if (wasPending && this.command && this.command.status !== 'meter_confirmed') {
          this.$notification.error('Valve update failed. The switch has been restored to the reported meter status.')
        }
      }
    },
    scheduleRefresh() {
      clearTimeout(this.pollTimer)
      if (this.stopped || this.submitting || !this.pending) return
      const meterId = this.meter.id
      const { requestVersion } = this
      this.pollTimer = setTimeout(() => {
        axios.get(`meters/${meterId}`)
          .then(({ data }) => {
            if (this.stopped || this.meter.id !== meterId || this.requestVersion !== requestVersion) return
            this.applyStatus(data)
          })
          .catch(() => {
            // Retry temporary refresh failures on the next polling cycle.
          })
          .finally(() => {
            if (!this.stopped && this.meter.id === meterId && this.requestVersion === requestVersion) this.scheduleRefresh()
          })
      }, 5000)
    },
    submitChange() {
      if (this.submitting || this.requestedPosition === null) return
      this.showConfirm = false
      this.submitting = true
      clearTimeout(this.pollTimer)
      this.requestVersion += 1
      const { requestVersion } = this
      const meterId = this.meter.id
      const requested = this.requestedPosition
      this.optimisticPosition = requested
      this.switchPosition = requested
      axios.put(`valve-status/${meterId}`, { valve_status: requested })
        .then(response => {
          if (this.stopped || this.meter.id !== meterId || this.requestVersion !== requestVersion) return
          if (response.status === 202 || response.data.status === 'pending') {
            this.applyStatus(response.data)
            this.scheduleRefresh()
          } else {
            this.meter.valve_status = requested
            this.$set(this.meter, 'valve_command', null)
            this.optimisticPosition = null
            this.$notification.success(`Valve ${requested === 1 ? 'opened' : 'closed'} successfully`)
          }
        })
        .catch(error => {
          if (this.stopped || this.meter.id !== meterId || this.requestVersion !== requestVersion) return
          this.optimisticPosition = null
          this.switchPosition = this.displayedPosition
          this.$notification.error((error.response && error.response.data.message) || 'Unable to update valve. The switch has been restored.')
        })
        .finally(() => {
          if (this.stopped || this.meter.id !== meterId || this.requestVersion !== requestVersion) return
          this.submitting = false
          this.requestedPosition = null
          this.scheduleRefresh()
        })
    },
  },
}
</script>
